import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Badge, Button, ListGroup, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Plus, Link } from 'lucide-react';
import { getDatabase, ref as dbRef, get } from 'firebase/database';
import { WorkOrder, WorkOrderStatus, WorkOrderPriority, workOrderPriorityColors, getComponentKeys, getAssignees, getDelegations } from '../../../types/workOrders';
import { sortDelmoment } from '../../../Helpers/projectBoard';
import { Building, BuildingModel } from '../../../types/protocols';
import { BaseComponentImpl } from '../../../types/implementations';
import { resolveComponentLocation } from '../../../Helpers/ComponentRoomUtils';
import { translateWorkOrderPriority, translateWorkOrderStatus } from '../../../types/EnumTranslations';
import { formatDateForDisplay } from '../../../Helpers/DateUtils';
import { useBuildComponentTypes } from '../../../contexts/BuildComponentTypesContext';
import { useUserNames } from '../../../Helpers/useUserNames';
import { useProjectWorkOrders } from '../../../hooks/projects/useProjectWorkOrders';
import WorkOrderDetailModal from '../../Förvaltning/components/WorkOrderDetailModal';
import CreateWorkOrderModal from '../../Förvaltning/components/CreateWorkOrderModal';
import DelmomentProgressRing from '../../Förvaltning/components/DelmomentProgressRing';
import { MetadataInfoButton, MetadataEntry } from '../../../Components/MetadataInfoButton';
import LinkWorkOrdersModal, { workOrderAssignmentSummary } from './LinkWorkOrdersModal';

interface ProjectWorkOrdersSectionProps {
    buildingKey: string;
    projectKey: string;
}

export const ProjectWorkOrdersSection: React.FC<ProjectWorkOrdersSectionProps> = ({
    buildingKey,
    projectKey
}) => {
    const { userNames, loadUserNames } = useUserNames();
    const { getTranslation } = useBuildComponentTypes();
    const [componentInfo, setComponentInfo] = useState<Record<string, { name: string; location: string | null }>>({});
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedWorkOrderKey, setSelectedWorkOrderKey] = useState<string | null>(null);

    const {
        activeWorkOrders,
        archivedWorkOrders,
        unlinkedWorkOrders,
        loading,
        error,
        linkWorkOrder,
        refetch
    } = useProjectWorkOrders(buildingKey, projectKey);

    // Memoize work order UIDs to prevent useEffect re-runs
    const workOrderUids = useMemo(() => {
        const allWorkOrders = [...activeWorkOrders, ...archivedWorkOrders, ...unlinkedWorkOrders];
        const uids: string[] = [];
        allWorkOrders.forEach(wo => {
            if (wo.createdBy) uids.push(wo.createdBy);
            if (wo.updatedBy) uids.push(wo.updatedBy);
            if (wo.completedBy) uids.push(wo.completedBy);
            // Multi-assignee: all internal assignees + all external delegates.
            // Externals are also in users/userList, so loadUserNames resolves them.
            uids.push(...Object.keys(getAssignees(wo)));
            uids.push(...Object.keys(getDelegations(wo)));
        });
        return uids;
    }, [activeWorkOrders, archivedWorkOrders, unlinkedWorkOrders]);

    // Load user names for all work orders
    useEffect(() => {
        if (workOrderUids.length > 0) {
            loadUserNames(workOrderUids);
        }
    }, [workOrderUids, loadUserNames]);

    // User name lookup function for modal and displays
    const getUserName = useCallback((uid: string): string => {
        return userNames[uid] || 'Okänd användare';
    }, [userNames]);

    // Collect all component keys linked across the project's work orders
    const linkedComponentKeys = useMemo(() => {
        const keys = new Set<string>();
        [...activeWorkOrders, ...archivedWorkOrders].forEach(wo => {
            getComponentKeys(wo).forEach(key => keys.add(key));
        });
        return Array.from(keys);
    }, [activeWorkOrders, archivedWorkOrders]);
    const linkedComponentKeysSignature = linkedComponentKeys.join(',');

    // Fetch display name + location (level/room) for all linked components.
    // The whole structure is fetched once so room and level can be resolved
    // (resolveComponentLocation also needs sibling components to follow parent
    // surfaces), rather than reading each component in isolation.
    useEffect(() => {
        if (!buildingKey || linkedComponentKeys.length === 0) {
            setComponentInfo({});
            return;
        }

        let cancelled = false;
        const fetchComponentInfo = async () => {
            try {
                const db = getDatabase();
                const structureRef = dbRef(db, `buildings/${buildingKey}/structure`);
                const snapshot = await get(structureRef);
                const structure = (snapshot.val() ?? {}) as NonNullable<BuildingModel['structure']>;
                const components = structure.components ?? {};

                // Use the component's own displayName so the project's delmoment
                // list matches the work-order views. displayName resolves the
                // specific type via the global component-types store (kept warm
                // by useBuildComponentTypes); getTranslation stays in the effect
                // deps so names recompute once types load.
                const building: Building = {
                    key: buildingKey,
                    summary: { key: buildingKey },
                    model: { key: buildingKey, structure },
                };

                const info: Record<string, { name: string; location: string | null }> = {};
                linkedComponentKeys.forEach((componentKey) => {
                    const componentData = components[componentKey];
                    if (!componentData) {
                        info[componentKey] = { name: 'Komponent', location: null };
                        return;
                    }
                    const impl = new BaseComponentImpl(componentData);
                    const loc = resolveComponentLocation(impl, structure);
                    const locationParts = [loc.levelDisplay, loc.roomDisplay].filter(Boolean);
                    info[componentKey] = {
                        name: impl.displayName(building),
                        location: locationParts.length > 0 ? locationParts.join(', ') : null,
                    };
                });

                if (!cancelled) {
                    setComponentInfo(info);
                }
            } catch (err) {
                console.error('Error fetching component info:', err);
                if (!cancelled) {
                    setComponentInfo({});
                }
            }
        };

        fetchComponentInfo();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkedComponentKeysSignature, buildingKey, getTranslation]);

    // Link the chosen loose work orders to this project (shared modal)
    const handleLink = async (workOrderKeys: string[]) => {
        await Promise.all(workOrderKeys.map(key => linkWorkOrder(key)));
    };

    const getPriorityBadge = (priority: WorkOrderPriority) => {
        const textProp = priority === WorkOrderPriority.MEDIUM ? 'dark' : undefined;
        return (
            <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Prioritet: {translateWorkOrderPriority(priority)}</Tooltip>}
            >
                <Badge bg={workOrderPriorityColors[priority]} text={textProp}>
                    {translateWorkOrderPriority(priority)}
                </Badge>
            </OverlayTrigger>
        );
    };

    const getStatusBadge = (status: WorkOrderStatus) => {
        // Only show a status tag once the work order is completed; open work
        // orders are the default state and don't need an "Öppen" tag.
        if (status !== WorkOrderStatus.COMPLETED) {
            return null;
        }
        return (
            <Badge bg="success">
                {translateWorkOrderStatus(status)}
            </Badge>
        );
    };

    const buildWorkOrderMetadataEntries = (wo: WorkOrder): MetadataEntry[] => {
        const entries: MetadataEntry[] = [];
        if (wo.createdBy) {
            entries.push({
                label: 'Skapad',
                date: wo.createdAt,
                userName: getUserName(wo.createdBy),
            });
        }
        if (wo.updatedBy && wo.updatedAt) {
            entries.push({
                label: 'Uppdaterad',
                date: wo.updatedAt,
                userName: getUserName(wo.updatedBy),
            });
        }
        if (wo.completedBy && wo.completedAt) {
            entries.push({
                label: 'Slutförd',
                date: wo.completedAt,
                userName: getUserName(wo.completedBy),
            });
        }
        return entries;
    };

    const renderWorkOrderItem = (wo: WorkOrder) => {
        const metadataEntries = buildWorkOrderMetadataEntries(wo);
        const componentKeys = getComponentKeys(wo);
        return (
            <ListGroup.Item
                key={wo.key}
                action
                onClick={() => setSelectedWorkOrderKey(wo.key)}
                className="d-flex justify-content-between align-items-start"
            >
                <div>
                    <div className="fw-medium">{wo.title}</div>
                    <small className="text-muted">
                        {wo.dueDate && `Förfallodatum: ${formatDateForDisplay(wo.dueDate)}`}
                        {workOrderAssignmentSummary(wo, getUserName) && ` • ${workOrderAssignmentSummary(wo, getUserName)}`}
                    </small>
                    {componentKeys.length > 0 && (
                        <div className="mt-1">
                            {componentKeys.map(key => {
                                const info = componentInfo[key];
                                const name = info?.name ?? 'Komponent';
                                return (
                                    <small key={key} className="text-muted d-block">
                                        {name}{info?.location ? ` (${info.location})` : ''}
                                    </small>
                                );
                            })}
                        </div>
                    )}
                </div>
                <div className="d-flex gap-1 align-items-center">
                    {metadataEntries.length > 0 && (
                        <div onClick={(e) => e.stopPropagation()}>
                            <MetadataInfoButton
                                entries={metadataEntries}
                            />
                        </div>
                    )}
                    {getPriorityBadge(wo.priority)}
                    {getStatusBadge(wo.status)}
                </div>
            </ListGroup.Item>
        );
    };

    const totalCount = activeWorkOrders.length + archivedWorkOrders.length;

    // Progress excludes cancelled delmoment so the ring reads as "completed of
    // the work that's still in scope" (open + completed).
    const completedCount = archivedWorkOrders.filter(
        wo => wo.status === WorkOrderStatus.COMPLETED
    ).length;
    const progressTotal = activeWorkOrders.length + completedCount;

    return (
        <div style={{
            marginTop: '24px',
            paddingTop: '24px',
            borderTop: '1px solid #dee2e6'
        }}>
            <div style={{ border: "1px solid #dee2e6", borderRadius: "10px", backgroundColor: "#fff" }}>
                <div style={{
                    backgroundColor: '#f8f9fa',
                    padding: '12px 16px',
                    borderBottom: '1px solid #dee2e6',
                    borderTopLeftRadius: '10px',
                    borderTopRightRadius: '10px'
                }}>
                    <div className="project-delmoment-header">
                        <h5 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                            Delmoment
                            <DelmomentProgressRing completed={completedCount} total={progressTotal} />
                        </h5>
                        <div className="project-delmoment-actions">
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => setShowCreateModal(true)}
                            >
                                <Plus size={14} className="me-1" />
                                Skapa delmoment
                            </Button>
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => setShowLinkModal(true)}
                            >
                                <Link size={14} className="me-1" />
                                Koppla ärende
                            </Button>
                        </div>
                    </div>
                </div>
                <div style={{ padding: '24px' }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                            <Spinner animation="border" size="sm" />
                        </div>
                    ) : error ? (
                        <div className="text-danger">{error}</div>
                    ) : totalCount === 0 ? (
                        <p style={{ color: '#666', fontStyle: 'italic' }}>
                            Inga delmoment kopplade till detta projekt.
                        </p>
                    ) : (
                        <ListGroup>
                            {sortDelmoment([...activeWorkOrders, ...archivedWorkOrders]).map(renderWorkOrderItem)}
                        </ListGroup>
                    )}
                </div>
            </div>

            {/* Link existing work orders modal */}
            <LinkWorkOrdersModal
                show={showLinkModal}
                onHide={() => setShowLinkModal(false)}
                unlinkedWorkOrders={unlinkedWorkOrders}
                onLink={handleLink}
                getUserName={getUserName}
            />

            {/* Work order detail modal */}
            {selectedWorkOrderKey && (
                <WorkOrderDetailModal
                    show={!!selectedWorkOrderKey}
                    onHide={() => setSelectedWorkOrderKey(null)}
                    workOrderKey={selectedWorkOrderKey}
                    onCompleted={() => {
                        setSelectedWorkOrderKey(null);
                        refetch();
                    }}
                    onCancelled={() => {
                        setSelectedWorkOrderKey(null);
                        refetch();
                    }}
                    onDeleted={() => {
                        setSelectedWorkOrderKey(null);
                        refetch();
                    }}
                    onChanged={() => {
                        // In-modal edit changed a field shown in the delmoment list
                        // (title, due date, assignee, priority, project link,
                        // components). Refresh the list without closing the modal —
                        // the one-time fetch won't reflect it otherwise.
                        refetch();
                    }}
                    getUserName={getUserName}
                />
            )}

            {/* Create work order modal */}
            <CreateWorkOrderModal
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
                project={{ key: projectKey, buildingKey }}
                onSuccess={() => {
                    setShowCreateModal(false);
                    refetch();
                }}
            />
        </div>
    );
};
